import React, { useCallback, useEffect, useState } from 'react';
import useTranslate from 'application/hooks/use-translate';
import Section from 'application/components/form-controls/section';
import Button from 'application/components/button';
import FieldContainer from 'application/components/form-controls/field-container';
import PasswordInput from 'application/components/form-controls/password-input';
import TextInput from 'application/components/form-controls/text-input';
import DangerZone from 'application/components/danger-zone';
import AuthenticationServiceProvider from 'app/infrastructure/authentication/service/AuthenticationServiceProvider';
import { MfaChallenge } from 'shared/domain/authentication/service/AuthenticationService';
import { useSelector } from 'react-redux';
import useValidatedForm from 'application/hooks/use-validated-form';
import useToast from 'application/hooks/use-toast';

interface UserReduxState {
  user?: {
    profile?: { email?: string };
  };
}

const SettingsSecurityPage = () => {
  const translate = useTranslate('pages.settings.security');
  const userEmail = useSelector<UserReduxState, string>((state) => state.user?.profile?.email ?? '');

  const { isValid: formIsValid, registerField, reset: resetValidations } = useValidatedForm();

  const [currentPasswordIsInvalid, setCurrentPasswordIsInvalid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaCodeIsInvalid, setMfaCodeIsInvalid] = useState(false);

  const { addToast } = useToast();

  const currentPasswordField = registerField('currentPassword');
  const newPasswordField = registerField('newPassword');
  const confirmPasswordField = registerField('confirmPassword');

  useEffect(() => setCurrentPasswordIsInvalid(false), [currentPassword]);
  useEffect(() => setMfaCodeIsInvalid(false), [mfaCode]);

  const updatePasswordAndNotify = async () => {
    await AuthenticationServiceProvider.getInstance().updatePassword(userEmail, newPassword);

    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setMfaChallenge(null);
    setMfaCode('');
    resetValidations();

    addToast({
      autoDismiss: true,
      message: translate('saved-success'),
      type: 'success'
    });
  };

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      return;
    }

    try {
      setIsLoading(true);
      const authService = AuthenticationServiceProvider.getInstance();
      const challenge = await authService.signIn(userEmail, currentPassword, 'password-submit-button');

      if (challenge) {
        setMfaCode('');
        setMfaChallenge(challenge);
        return;
      }

      await updatePasswordAndNotify();
    } catch (_error) {
      setCurrentPasswordIsInvalid(true);

      addToast({
        autoDismiss: true,
        message: translate('saved-error'),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!mfaChallenge || !mfaCode) {
      return;
    }

    setIsLoading(true);

    try {
      await AuthenticationServiceProvider.getInstance().mfaSignIn(mfaCode, mfaChallenge);
    } catch (_error) {
      setMfaCodeIsInvalid(true);
      setIsLoading(false);
      return;
    }

    try {
      await updatePasswordAndNotify();
    } catch (_error) {
      addToast({
        autoDismiss: true,
        message: translate('saved-error'),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaCancel = () => {
    setMfaChallenge(null);
    setMfaCode('');
  };

  const handleLogoutAllDevices = useCallback(() => {
    AuthenticationServiceProvider.getInstance()
      .logoutFromAllDevices()
      .catch(() => {
        AuthenticationServiceProvider.getInstance().signOut();
      });
  }, []);

  return (
    <form className="flex flex-col gap-4" onSubmit={mfaChallenge ? handleMfaSubmit : handlePasswordSubmit}>
      <Section label={translate('title')}>
        {mfaChallenge ? (
          <>
            <p className="px-4 pt-4 text-sm text-neutral-900">{translate('mfa-explanation')}</p>
            <FieldContainer label={translate('mfa-code')} required>
              <TextInput
                touched={mfaCodeIsInvalid}
                customValidation={
                  mfaCodeIsInvalid ? { isValid: false, errorMessage: translate('invalid-mfa-code') } : undefined
                }
                value={mfaCode}
                onChange={setMfaCode}
                required
              />
            </FieldContainer>
            <Section.Footer>
              <Button type="button" onClick={handleMfaCancel} className="btn btn-link">
                {translate('mfa-cancel')}
              </Button>
              <Button type="submit" disabled={!mfaCode} loading={isLoading} className="btn btn-primary">
                {translate('mfa-confirm')}
              </Button>
            </Section.Footer>
          </>
        ) : (
          <>
            <FieldContainer label={translate('current-password')} required>
              <PasswordInput
                {...currentPasswordField}
                customValidation={
                  currentPasswordIsInvalid
                    ? { isValid: false, errorMessage: translate('invalid-current-password') }
                    : undefined
                }
                value={currentPassword}
                onChange={setCurrentPassword}
                required
              />
            </FieldContainer>
            <FieldContainer label={translate('new-password')} required>
              <PasswordInput
                {...newPasswordField}
                value={newPassword}
                onChange={setNewPassword}
                validateStrength
                required
              />
            </FieldContainer>
            <FieldContainer label={translate('confirm-password')} required>
              <PasswordInput
                {...confirmPasswordField}
                customValidation={
                  confirmPassword
                    ? {
                        isValid: newPassword === confirmPassword,
                        errorMessage: translate('invalid-confirm-password')
                      }
                    : undefined
                }
                value={confirmPassword}
                onChange={setConfirmPassword}
                required
              />
            </FieldContainer>
            <Section.Footer>
              <Button
                id="password-submit-button"
                type="submit"
                disabled={!formIsValid}
                loading={isLoading}
                className="btn btn-primary"
              >
                {translate('save')}
              </Button>
            </Section.Footer>
          </>
        )}
      </Section>
      <DangerZone
        label={translate('logout-all-devices.label')}
        helper={translate('logout-all-devices.helper')}
        buttonLabel={translate('logout-all-devices.logout')}
        onClick={handleLogoutAllDevices}
        icon="shield-check"
      />
    </form>
  );
};

export default SettingsSecurityPage;
